import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useEffect } from "react";
import { BsApple } from "react-icons/bs";
import { FaFacebookF } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";

import { clearPendingBrAction } from "@/site/components/birthday-request/pending-br-action";
import { oauthErrorMessage } from "./auth-utils";
import { FormErrorMessage } from "./form-error";
import { useOAuthPopup } from "./use-oauth-popup";

/** MUI-contained parity base for the three provider pills (old .auth-social-* button). */
const socialButton =
  "flex h-10 cursor-pointer items-center justify-center rounded-[2rem] text-[14px] font-medium transition-colors";
/** Labelled pill (the default) vs the 40px square used in the authwall's expanded state. */
const sizeFor = (compact: boolean) => (compact ? "w-10 shrink-0" : "w-full");

const APPLE_REASON_ID = "apple-signin-unavailable-reason";

const APPLE_LOCAL_REASON =
  "Apple requires an https Return URL and rejects localhost, so Apple sign-in can't run on a local dev server. Use Google, Facebook or email here.";

/**
 * Whether Apple's Return URL rules can be satisfied by the current origin.
 *
 * `redirect_uri` is derived from the server's `BASE_URL`, and Apple refuses to
 * register or redirect to anything that isn't https on a real host — so on
 * `http://localhost:5001` the button can only ever end in an error page. Testing
 * the live origin rather than an env flag means this re-enables itself for free
 * on dev/staging/prod, and on a local https tunnel.
 */
export function isAppleSignInAvailable(): boolean {
  if (typeof window === "undefined") return true;
  const { protocol, hostname } = window.location;
  if (protocol !== "https:") return false;
  return (
    !["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"].includes(hostname) &&
    !hostname.endsWith(".local")
  );
}

/**
 * The three OAuth pills — Google, Apple, Facebook, in that order (Google leads
 * as the priority provider), in the 199px column the old
 * `.auth-social-container` used.
 *
 * Shared by the auth wall (`/login`, `/register`) and `RegisterBlock` (the
 * save-card and Birthday-Request funnels) so the buttons can't drift apart.
 *
 * Sign-in runs in a popup over our own endpoints (`useOAuthPopup`), so the page
 * underneath is never unloaded — a half-written sticky note, a chosen card, the
 * dialog you were standing in all survive. That's what legacy got from the
 * provider JS SDKs, without taking on the SDKs.
 */
export function AuthSocial({
  onBeforeNavigate,
  className,
  compact = false,
  inPlaceOnly = false,
}: {
  /** Runs just before leaving the page, e.g. to stash a pending action to resume. */
  onBeforeNavigate?: () => void;
  /** Container override — the auth wall runs a tighter gap than RegisterBlock. */
  className?: string;
  /**
   * Icon-only row instead of the labelled stack (legacy `AuthSocial withIcons`).
   * Used by the in-flow authwall once the email form is revealed, where the three
   * providers become a compact row above the form rather than the main event.
   */
  compact?: boolean;
  /**
   * Never navigate the page (the in-flow authwall): a blocked popup shows an inline
   * "allow pop-ups and try again" with a retry, instead of the full-page redirect.
   */
  inPlaceOnly?: boolean;
}) {
  const { start, pending, error, cancelled, blocked, redirect } = useOAuthPopup({
    inPlaceOnly,
  });
  // `onBeforeNavigate` stashes a pending action (e.g. the birthday-cake request)
  // before the popup opens. If the member closes the popup without signing in,
  // drop it — otherwise it fires unannounced on whatever their next sign-in is.
  useEffect(() => {
    if (cancelled) clearPendingBrAction();
  }, [cancelled]);
  const startOAuth = (provider: "apple" | "google" | "facebook") => {
    // Still fires: callers use it to stash a pending action, and the blocked-popup
    // fallback is a real full-page navigation.
    onBeforeNavigate?.();
    start(provider);
  };

  const appleAvailable = isAppleSignInAvailable();
  // While a popup is open the page stays interactive, so guard against a second
  // one being opened on top of it.
  const busy = pending !== null;
  const appleButton = (
    <button
      type="button"
      // `aria-disabled` rather than `disabled` when it's the localhost block: a
      // disabled button leaves the tab order, so a keyboard user could neither
      // reach it nor ever hear the tooltip explaining why it's off.
      disabled={busy}
      aria-disabled={!appleAvailable || undefined}
      aria-describedby={!appleAvailable ? APPLE_REASON_ID : undefined}
      onClick={() => appleAvailable && startOAuth("apple")}
      className={cn(
        socialButton,
        sizeFor(compact),
        "bg-black text-white hover:bg-[#333333]",
        (!appleAvailable || busy) && "cursor-not-allowed opacity-50 hover:bg-black",
      )}
      aria-label={compact ? "Continue with Apple" : undefined}
      data-testid="button-login-apple"
    >
      <BsApple
        className={compact ? "size-[1.3em]" : "mt-[-3px] mr-[9px] ml-[-12px] size-[1.3em]"}
      />
      {!compact && (
        <span>{pending === "apple" ? "Waiting for Apple…" : "Continue with Apple"}</span>
      )}
    </button>
  );

  return (
    <div
      className={cn(
        "flex w-full items-center justify-center",
        compact ? "flex-row gap-8" : "max-w-[199px] flex-col gap-4",
        compact && blocked && "flex-wrap",
        className,
      )}
    >
      <button
        type="button"
        disabled={busy}
        onClick={() => startOAuth("google")}
        className={cn(
          socialButton,
          sizeFor(compact),
          "border border-[#dadce0] bg-white text-foreground hover:border-[#d2e3fc] hover:bg-[#f1f5ff]",
          busy && "cursor-not-allowed opacity-50",
        )}
        aria-label={compact ? "Continue with Google" : undefined}
        data-testid="button-login-google"
      >
        <FcGoogle className={compact ? "size-[1.3em]" : "mr-[6px] ml-[-6px] size-[1.3em]"} />
        {!compact && (
          <span>{pending === "google" ? "Waiting for Google…" : "Continue with Google"}</span>
        )}
      </button>
      {appleAvailable ? (
        appleButton
      ) : (
        <>
          <Tooltip>
            <TooltipTrigger
              // Compact row: the trigger must hug the 40px square — a full-width span
              // stretched across the row and pushed Facebook to the far edge.
              render={
                <span
                  className={compact ? "inline-flex shrink-0" : "w-full"}
                  data-testid="tooltip-apple-unavailable"
                />
              }
            >
              {appleButton}
            </TooltipTrigger>
            <TooltipContent>{APPLE_LOCAL_REASON}</TooltipContent>
          </Tooltip>
          {/* The tooltip is hover/focus-only; this is what `aria-describedby`
              on the button actually points at, so the reason is available to
              assistive tech too. */}
          <span id={APPLE_REASON_ID} className="sr-only">
            {APPLE_LOCAL_REASON}
          </span>
        </>
      )}
      <button
        type="button"
        disabled={busy}
        onClick={() => startOAuth("facebook")}
        className={cn(
          socialButton,
          sizeFor(compact),
          "bg-[#3b5998] text-white hover:bg-[#5278c9]",
          busy && "cursor-not-allowed opacity-50",
        )}
        aria-label={compact ? "Continue with Facebook" : undefined}
        data-testid="button-login-facebook"
      >
        <FaFacebookF
          className={compact ? "size-[1.3em]" : "mt-[-1px] mr-[4px] ml-[-3px] size-[1.3em]"}
        />
        {!compact && (
          <span className="tracking-[-0.4px]">
            {pending === "facebook" ? "Waiting for Facebook…" : "Continue with Facebook"}
          </span>
        )}
      </button>

      {/* In popup mode a failure never navigates, so it has to be shown here —
          the /login page's `?error=` handling only covers the fallback path. */}
      {error && <FormErrorMessage>{oauthErrorMessage(error)}</FormErrorMessage>}
      {blocked && (
        // Full width so it wraps under the compact icon row instead of joining it.
        <div
          role="alert"
          className="flex w-full flex-col items-center gap-2 text-center text-sm"
          data-testid="oauth-popup-blocked"
        >
          <p>Your browser blocked the sign-in window &mdash; allow pop-ups and try again.</p>
          <button
            type="button"
            onClick={() => startOAuth(blocked)}
            className="font-semibold text-primary underline"
            data-testid="button-oauth-retry"
          >
            Try again
          </button>
          {/* In-app browsers (Facebook, Instagram) never allow the popup. Leaving is
              their call, and `next` brings them back to this page. */}
          <button
            type="button"
            onClick={() => {
              onBeforeNavigate?.();
              redirect(blocked);
            }}
            className="text-muted-foreground underline"
          >
            Continue in this tab instead
          </button>
        </div>
      )}
    </div>
  );
}
